"use client";

import { api } from "../api";
import type { Banner, BannerPosition, Product } from "../types";
import { useAsync, type Async } from "../useAsync";

/*
 * 상품 · 배너 — 로그인과 무관하게 누구나 봅니다.
 *
 * 사용:
 *   const products = useProducts();          // products.data: Product[] | null
 *   const product = useProduct(id);          // product.data: Product | null
 *   const banners = useBanners("home_top");  // banners.data: Banner[] | null
 *   (셋 다 { data, loading, error, reload } — `useAsync` 모양)
 *
 * 🔑 **페이징이 없습니다** — 판매 상품은 수십 개이고, 계약(§3-2)도 `{items: [...]}` 입니다.
 */
export function useProducts(): Async<Product[]> {
  return useAsync(
    async (signal) => (await api.get<{ items: Product[] }>("/products", { signal })).items,
    "products",
  );
}

/** 🔑 `enabled` 가 거짓이면 부르지 않습니다 — 결제 화면이 로그인 확인을 기다릴 때. */
export function useProduct(id: string, enabled = true): Async<Product> {
  return useAsync(
    (signal) => api.get<Product>(`/products/${encodeURIComponent(id)}`, { signal }),
    `product-${id}`,
    enabled && id !== "",
  );
}

export function useBanners(position: BannerPosition): Async<Banner[]> {
  return useAsync(
    async (signal) => (await api.get<{ items: Banner[] }>("/banners", { query: { position }, signal })).items,
    `banners-${position}`,
  );
}
